<template>
  <div>
    <MembershipFormDialog
      ref="membershipFormDialog"
      :is-dialog-visible.sync="isDialogVisible"
      :enrollment-id="enrollmentId"
      :membership-dates="[]"
      @update="onAfterApproveMember"
    />

    <PageHeaderWithTab
      pre-title="회원"
      :title="pageTitle"
      :has-amount="true"
      :tab-menu="menus"
      @change="onTabMenuChange"
    />

    <el-table
      v-if="dataForTable.length"
      v-loading="loading"
      :data="dataForTable"
      style="width: 100%"
      class="fifl-main-table"
      header-row-class-name="fifl-table-header-row"
      header-cell-class-name="fifl-table-header-cell"
      row-class-name="fifl-table-row"
      cell-class-name="fifl-main-table-cell"
    >
      <el-table-column
        sortable
        prop="member.name"
        label="이름"
        width="95"
      />
      <el-table-column
        sortable
        prop="member.gender"
        label="성별"
        width="85"
      >
        <template slot-scope="scope">
          {{ scope.row.member.gender % 2 === 1 ? '남' : '여' }}
        </template>
      </el-table-column>
      <el-table-column
        sortable
        prop="member.birth"
        label="생년월일"
        width="90"
      />
      <el-table-column
        prop="member.phone_number"
        label="핸드폰 번호"
        width="95"
      />
      <el-table-column />
      <el-table-column
        prop="id"
        fixed="right"
        width="200"
      >
        <template slot-scope="scope">
          <div style="float: right; padding-right: 5px;">
            <template v-if="curMenu === WAITING_MEMBER_STATUS.PENDING">
              <el-button
                class="white"
                data-test="button-member-reject"
                @click="handleClickReject(scope.row.id)"
              >
                거절
              </el-button>
              <el-button
                class="gray"
                data-test="button-member-approve"
                @click="handleClickApprove(scope.row.id)"
              >
                승인
              </el-button>
            </template>
            <template v-else>
              <el-button
                class="white"
                @click="handleClickDelete(scope.row.id)"
              >
                영구 삭제
              </el-button>
              <el-button
                class="gray"
                @click="handleClickApprove(scope.row.id)"
              >
                재승인
              </el-button>
            </template>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <empty-table 
      v-else
      :props-loading-status="loading"
      :props-has-parent="emptyTableProps.hasParent"
      :props-is-icon="emptyTableProps.isIcon"
      :props-header-height="emptyTableProps.headerHeight"
      :props-data="emptyTableProps.data"
      :props-table-body-height="emptyTableProps.bodyHeight"
      :props-message="emptyTableProps.message" 
    />
  </div>
</template>

<script>
import EmptyTable from '@/components/EmptyTable';
import PageHeaderWithTab from '@/components/element-ui/PageHeaderWithTab';
import WarningMessage from '@/components/element-ui/WarningMessage';
import MembershipFormDialog from './MemberDetail/MembershipFormDialog';
import { computedGetters } from '@/utils/vuex-helper';
import { fiflboxApi } from '@/utils/api';

const WAITING_MEMBER_STATUS = {
  PENDING: 'PENDING',
  REJECT: 'REJECT'
};

export default {
  name: 'WaitingMember',
  components: {
    EmptyTable,
    MembershipFormDialog,
    WarningMessage,
    PageHeaderWithTab
  },
  data() {
    return {
      WAITING_MEMBER_STATUS,
      isDialogVisible: false,
      loading: false,
      searchKeyword: '',
      tableData: [],
      dataForTable: [],
      enrollmentId: null,
      emptyTableProps: {
        message: '등록 된 회원이 없습니다.',
        bodyHeight: 410,
        headerHeight: 43,
        hasParent: false,
        isIcon: true,
        data: [
          { label: '이름', width: 95 + 'px' },
          { label: '성별', width: 85 + 'px' },
          { label: '생년월일', width: 90 + 'px' },
          { label: '핸드폰 번호', width: 95 + 'px' },
          { label: '', width: 'calc(100% - 365px)' }
        ]
      },
      memberCount: {
        [WAITING_MEMBER_STATUS.PENDING]: 0,
        [WAITING_MEMBER_STATUS.REJECT]: 0
      },
      curMenu: WAITING_MEMBER_STATUS.PENDING
    };
  },
  watch: {
    isPendingMemberMenuVisible: {
      immediate: true,
      handler: function(isAccessAllowed) {
        if (!isAccessAllowed) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    }
  },
  computed: {
    ...computedGetters(['isPendingMemberMenuVisible', 'centerAccount']),
    menus() {
      return [
        {
          title: '대기',
          value: WAITING_MEMBER_STATUS.PENDING,
          amount: this.memberCount[WAITING_MEMBER_STATUS.PENDING]
        },
        {
          title: '거절',
          value: WAITING_MEMBER_STATUS.REJECT,
          amount: this.memberCount[WAITING_MEMBER_STATUS.REJECT]
        }
      ];
    },
    pageTitle() {
      return `승인 ${
        this.curMenu === WAITING_MEMBER_STATUS.PENDING ? '대기' : '거절'
      } 회원`;
    }
  },
  async mounted() {
    await this.syncMembersByStatus(this.curMenu);
    await this.syncAllCount();
  },
  methods: {
    async syncMembersByStatus(status) {
      this.loading = true;

      let { data: members } = await fiflboxApi.v1.getEnrollmentMembers({
        status
      });

      this.dataForTable = members;

      this.loading = false;
    },
    async syncMemberCountByStatus(status) {
      const {
        data: { count }
      } = await fiflboxApi.v1.getEnrollmentMembers({
        status,
        offset: 0,
        limit: 1
      });

      this.memberCount[status] = count;
    },
    async syncAllCount() {
      this.syncMemberCountByStatus(WAITING_MEMBER_STATUS.PENDING);
      this.syncMemberCountByStatus(WAITING_MEMBER_STATUS.REJECT);
    },
    onTabMenuChange(menu) {
      this.curMenu = menu.value;
      this.syncMembersByStatus(this.curMenu);
    },
    async onAfterApproveMember() {
      this.syncMemberCountByStatus(this.curMenu);
      this.syncMembersByStatus(this.curMenu);
      if (this.centerAccount.plan === 0) {
        this.$store.dispatch('syncCenterAccount');
      }

      try {
        await this.$confirm(
          '회원 상세 페이지로 이동하시겠습니까?',
          '승인 완료'
        );
        return this.$router
          .push(`/member/${this.enrollmentId}`)
          .catch(() => {});
      } catch (err) {}
    },
    handleClickApprove(enrollmentId) {
      this.enrollmentId = enrollmentId;
      this.$refs['membershipFormDialog'].openDialogToAdd({
        classification: 1
      });
    },
    async handleClickReject(enrollementId) {
      await fiflboxApi.v1.putEnrollmentMembers(enrollementId, {
        status: 'REJECT'
      });
      await this.syncMembersByStatus(this.curMenu);
      await this.syncAllCount();
    },
    async handleClickDelete(enrollementId) {
      this.$confirm(
        '영구 삭제 시 회원은 본 리스트에서 바로 사라지며 절대 복구 불가합니다. 동일 이메일 계정으로 재등록을 원하실 경우 회원이 앱에서 재승인 신청해야 합니다. 그럼에도 이 회원을 삭제하시겠습니까?',
        '회원 삭제',
        {
          confirmButtonText: '확인',
          cancelButtonText: '취소',
          showClose: false,
          customClass: 'fifl-message-box',
          confirmButtonClass: 'gray',
          cancelButtonClass: 'white',
          closeOnPressEscape: false
        }
      )
        .then(async e => {
          await fiflboxApi.v1.deleteEnrollmentMembers(enrollementId);
          await this.syncMembersByStatus(this.curMenu);
          await this.syncAllCount();
        })
        .catch(err => {});
    }
  }
};
</script>

<style>
</style>
